import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { signOut } from "firebase/auth";

import { ROLE_OBJECT_TYPE } from "@/common/enums/roleObjectType";
import { auth } from "@/common/platform/firebase";
import {
  checkIsGlobalManagementPermission,
  checkIsOrganizationManagementPermission,
} from "@/common/utilities/permission";
import type { RootState } from "@/state/types";

export type AuthenticatedUser = {
  email: string;
  displayName: string;
  emailVerified: boolean;
  photoURL: string | null;
  uid: string;
  isAnonymous: boolean;
  isNewUser?: boolean;
};

type SetUserPayload =
  | (Omit<AuthenticatedUser, "displayName"> & {
      displayName?: string | null;
    })
  | null;

export type ManagementPermission = {
  permission: string;
  objectType: string;
  objectId: string | number | null;
  organizationId: number | null;
};

type AuthenticationState = {
  user: AuthenticatedUser | null;
  userLoaded: boolean;
  dialogOpen: boolean;
  permissions: ManagementPermission[];
  permissionsLoaded: boolean;
};

const initialState: AuthenticationState = {
  user: null,
  userLoaded: false,
  dialogOpen: false,
  permissions: [],
  permissionsLoaded: false,
};

export const signout = createAsyncThunk("authentication/signout", async () => {
  await signOut(auth);
  // eslint-disable-next-line no-restricted-globals
  location.reload();
});

const permissionsToKey = (permission: ManagementPermission): string =>
  `${permission.objectType}/${permission.objectId}/${permission.organizationId}/${permission.permission}`;

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    setUser: (state, { payload: user }: PayloadAction<SetUserPayload>) => {
      state.user = user
        ? {
            ...user,
            displayName: user.displayName || user.email,
          }
        : null;
      state.userLoaded = true;
    },
    // TODO: We need another action to clear permissions based on objectType
    appendPermissions: (
      state,
      {
        payload: { permissions },
      }: PayloadAction<{ permissions: ManagementPermission[] }>
    ) => {
      const deduplicatedPermissions = new Map<string, ManagementPermission>();

      for (const permission of [...permissions, ...state.permissions]) {
        const permissionKey = permissionsToKey(permission);
        if (!deduplicatedPermissions.has(permissionKey)) {
          deduplicatedPermissions.set(permissionKey, permission);
        }
      }

      state.permissions = Array.from(deduplicatedPermissions.values());
      state.permissionsLoaded = true;
    },
    /**
     * A record's permissions as its detail response states them, in place of
     * what the store held for it: the backend lists every `<type>.*`
     * permission the user holds on the record there, so one it no longer
     * lists has been revoked (GEN5-5283). Management grants and permissions
     * of other kinds on the record (e.g. its attachments) are kept.
     */
    replaceRecordPermissions: (
      state,
      {
        payload: { objectType, objectId, permissions },
      }: PayloadAction<{
        objectType: string;
        objectId: string | number;
        permissions: ManagementPermission[];
      }>
    ) => {
      const isStated = (permission: ManagementPermission) =>
        permission.objectType === objectType &&
        String(permission.objectId) === String(objectId) &&
        permission.permission.startsWith(`${objectType}.`);
      const kept = state.permissions.filter(
        (permission) => !isStated(permission)
      );
      const keys = new Set(kept.map(permissionsToKey));
      state.permissions = [
        ...kept,
        ...permissions.filter(
          (permission) => !keys.has(permissionsToKey(permission))
        ),
      ];
      state.permissionsLoaded = true;
    },
    clearPermissions: (
      state,
      {
        payload: { permissions: toBeRemovedPermissions },
      }: PayloadAction<{ permissions: ManagementPermission[] }>
    ) => {
      const toBeRemovedPermissionsKeys = new Set(
        toBeRemovedPermissions.map(permissionsToKey)
      );
      state.permissions = state.permissions.filter(
        (statePermission) =>
          !toBeRemovedPermissionsKeys.has(permissionsToKey(statePermission))
      );
      state.permissionsLoaded = true;
    },
    openSigninDialog: (state) => {
      state.dialogOpen = true;
    },
    closeSigninDialog: (state) => {
      state.dialogOpen = false;
    },
  },
});

export const {
  setUser,
  openSigninDialog,
  closeSigninDialog,
  appendPermissions,
  replaceRecordPermissions,
  clearPermissions,
} = authenticationSlice.actions;

export const selectUser = (state: RootState): AuthenticatedUser | null =>
  state.authentication.user;
export const selectUserLoaded = (state: RootState): boolean =>
  state.authentication.userLoaded;
export const selectPermissions = (state: RootState): ManagementPermission[] =>
  state.authentication.permissions;
export const selectHasGlobalManagementPermission =
  (permission: string) =>
  (state: RootState): boolean =>
    state.authentication.permissions.some(
      (p) => p.permission === permission && checkIsGlobalManagementPermission(p)
    );
export const selectHasOrganizationManagementPermission =
  (permission: string) =>
  (state: RootState): boolean =>
    state.authentication.permissions.some(
      (p) =>
        p.permission === permission &&
        checkIsOrganizationManagementPermission(p)
    );

export const selectHasObjectPermissions =
  (
    objectType: string | undefined,
    objectId: string | number | null | undefined,
    // Nav entries without a permission requirement pass `undefined`, which
    // matches no stored permission.
    permission: string | undefined
  ) =>
  (state: RootState): boolean =>
    state.authentication.permissions
      .filter((p) => {
        if (
          checkIsGlobalManagementPermission(p) ||
          checkIsOrganizationManagementPermission(p)
        ) {
          return true;
        }

        return (
          p.objectType === objectType &&
          p.objectId?.toString() === objectId?.toString()
        );
      })
      .some(({ permission: p }) => p === permission);

export const selectAllAccessibleWorkspaceIdsByPermissions = createSelector(
  [selectPermissions],
  (permissions): { numWorkspaces: number; workspaceIds: Set<string> } => {
    const workspaceIds = new Set<string>();

    permissions.forEach((p) => {
      if (
        checkIsGlobalManagementPermission(p) ||
        checkIsOrganizationManagementPermission(p)
      ) {
        workspaceIds.add("*");
      }

      if (
        p.objectType === ROLE_OBJECT_TYPE.PROJECT &&
        p.objectId !== null &&
        p.objectId !== undefined
      ) {
        workspaceIds.add(p.objectId.toString());
      }
    });

    const numWorkspaces = Array.from(workspaceIds).filter(
      (workspaceId) => workspaceId !== "*"
    ).length;

    return { numWorkspaces, workspaceIds };
  }
);

export const selectHasPermissions =
  (
    objectType: string,
    objectIds: ReadonlyArray<string | number>,
    permission: string
  ) =>
  (state: RootState): Record<string, boolean> =>
    objectIds.reduce<Record<string, boolean>>(
      (acc, objectId) => ({
        ...acc,
        [objectId]: selectHasObjectPermissions(
          objectType,
          objectId,
          permission
        )(state),
      }),
      {}
    );
export const selectPermissionsLoaded = (state: RootState): boolean =>
  state.authentication.permissionsLoaded;
export const selectSignInDialogOpen = (state: RootState): boolean =>
  state.authentication.dialogOpen;

export default authenticationSlice.reducer;
